import {useState, ChangeEvent, useCallback} from 'react';
import {AppConfiguration, PakConfiguration} from './pineridge-channel';
import {TransitionModal} from './transition-modal';
import {TEXT_INPUT_STYLE, BUTTON_STYLE_MAIN_FLEX, BUTTON_STYLE_CANCEL_FLEX, BUTTON_STYLE_MODAL} from './styles';
import {PakConfigInputs} from './pak-config-inputs';

type AppConfigModalProps = {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  initialConfig: AppConfiguration;
  setConfig: (config: AppConfiguration) => void;
  setUserAgent: (userAgent: string) => void;
};

export function AppConfigModal({isOpen, setOpen, initialConfig, setConfig, setUserAgent}: AppConfigModalProps) {
  const [pakConfig, setPakConfig] = useState<PakConfiguration>(initialConfig);
  const [metadata, setMetadata] = useState(initialConfig.metadata);
  const initialUserAgentValue = initialConfig.userAgent || '';
  const [userAgentValue, setUserAgentValue] = useState(initialUserAgentValue);
  const [isUserAgentSet, setIsUserAgentSet] = useState(false);
  const [hasInputError, setHasInputError] = useState(false);

  function cancel(): void {
    setPakConfig(initialConfig);
    setUserAgentValue(isUserAgentSet ? userAgentValue : initialUserAgentValue);
    setHasInputError(false);

    setOpen(false);
  }

  function submit(): void {
    setConfig({
      ...pakConfig,
      metadata,
      userAgent: userAgentValue && userAgentValue.length > 0 ? userAgentValue : undefined,
    });
    setOpen(false);
  }

  const handleUpdatePakConfig = useCallback((config: PakConfiguration) => {
    setPakConfig(config);
  }, []);

  const handlePakConfigError = useCallback((hasError: boolean) => {
    setHasInputError(hasError);
  }, []);

  function handleChangeMetadata(e: ChangeEvent<HTMLTextAreaElement>): void {
    const value = e.target.value;
    setMetadata(value);
  }

  function handleChangeUserAgent(e: ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    setUserAgentValue(value);
  }

  function handleSetUserAgent(): void {
    if (userAgentValue && !isUserAgentSet) {
      setUserAgent(userAgentValue);
      setIsUserAgentSet(true);
    }
  }

  return (
    <TransitionModal isOpen={isOpen} onClose={cancel} title="Configure App Defaults">
      <PakConfigInputs
        isOpen={isOpen}
        initialConfig={initialConfig}
        updateConfig={handleUpdatePakConfig}
        updateConfigError={handlePakConfigError}
        disabled={false}
      />

      <div className="mt-2 text-sm grid grid-cols-3">
        <div className="mr-1 mb-1 font-bold">User Agent</div>
        <div className="mr-1 mb-1">
          <input
            className={TEXT_INPUT_STYLE}
            value={userAgentValue}
            onChange={handleChangeUserAgent}
            disabled={isUserAgentSet}
          />
        </div>
        <div className="mb-1">
          <button
            onClick={handleSetUserAgent}
            disabled={isUserAgentSet || !userAgentValue || userAgentValue.length === 0}
            className={BUTTON_STYLE_MODAL}
          >
            Set
          </button>
        </div>

        <div className="mr-1 mb-1 font-bold">Metadata</div>
        <div>{/* This is just here to fill out the grid row */}</div>
        <div>{/* This is just here to fill out the grid row */}</div>
        <div className="col-span-3">
          <textarea className={TEXT_INPUT_STYLE} value={metadata} onChange={handleChangeMetadata} />
        </div>
      </div>
      <div className="px-4 py-2 flex flex-row-reverse sm:px-6">
        <button type="button" className={BUTTON_STYLE_MAIN_FLEX + ' m-1'} onClick={submit} disabled={hasInputError}>
          Submit
        </button>
        <button type="button" className={BUTTON_STYLE_CANCEL_FLEX + ' m-1'} onClick={cancel}>
          Cancel
        </button>
      </div>
    </TransitionModal>
  );
}
